import type { CobranzaFilter, CobranzaSort, UnitCobranza } from '@yawi/types';
import { Search, Building2 } from 'lucide-react';
import React from 'react';
import { UnitRow } from './UnitRow';

interface UnidadesTableProps {
  units: UnitCobranza[];
  loading: boolean;
  filter: CobranzaFilter;
  query: string;
  onQueryChange: (value: string) => void;
  sort: CobranzaSort;
  onSortChange: (value: CobranzaSort) => void;
  selectedIds: string[];
  onToggleSelect: (id: string) => void;
  onSelectVisible: (ids: string[]) => void;
  onSelectWithDebt: () => void;
  onClearSelection: () => void;
  pendingAccessId: string | null;
  onToggleAccess: (unit: UnitCobranza) => void;
}

const FILTER_LABEL: Record<CobranzaFilter, string> = {
  all: 'todas',
  withDebt: 'con adeudo',
  blocked: 'bloqueadas',
  current: 'al día',
};

export const UnidadesTable: React.FC<UnidadesTableProps> = ({
  units,
  loading,
  filter,
  query,
  onQueryChange,
  sort,
  onSortChange,
  selectedIds,
  onToggleSelect,
  onSelectVisible,
  onSelectWithDebt,
  onClearSelection,
  pendingAccessId,
  onToggleAccess,
}) => {
  const selectedSet = new Set(selectedIds);
  const allVisibleSelected = units.length > 0 && units.every((u) => selectedSet.has(u.id));

  const handleSelectAll = () => {
    if (allVisibleSelected) {
      onClearSelection();
    } else {
      onSelectVisible(units.map((u) => u.id));
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white">
      <div className="flex flex-col gap-3 border-b border-slate-100 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Buscar por nombre, correo o unidad"
            aria-label="Buscar unidades"
            className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm focus:border-[#7C5CFC] focus:outline-none focus:ring-1 focus:ring-[#7C5CFC]"
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onSelectWithDebt}
            className="cursor-pointer rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
          >
            Seleccionar con adeudo
          </button>
          <select
            value={sort}
            onChange={(e) => onSortChange(e.target.value as CobranzaSort)}
            aria-label="Ordenar unidades"
            className="cursor-pointer rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-[#7C5CFC] focus:outline-none focus:ring-1 focus:ring-[#7C5CFC]"
          >
            <option value="priority">Prioridad (monto)</option>
            <option value="name">Unidad (A-Z)</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="divide-y divide-slate-100">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 p-4">
              <div className="h-9 w-9 animate-pulse rounded-full bg-slate-100" />
              <div className="flex-1 space-y-2">
                <div className="h-3 w-1/3 animate-pulse rounded bg-slate-100" />
                <div className="h-3 w-1/4 animate-pulse rounded bg-slate-100" />
              </div>
            </div>
          ))}
        </div>
      ) : units.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 p-12 text-center text-slate-400">
          <Building2 size={32} strokeWidth={1.5} />
          <p className="text-sm">No hay unidades que coincidan con el filtro.</p>
        </div>
      ) : (
        <>
          <p className="px-4 pt-3 text-xs font-medium text-slate-400">
            Mostrando {FILTER_LABEL[filter]} · {units.length} unidad
            {units.length === 1 ? '' : 'es'}
          </p>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px]">
              <thead>
                <tr className="text-left text-xs font-medium uppercase tracking-wide text-slate-400">
                  <th className="px-4 py-2">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={handleSelectAll}
                      aria-label="Seleccionar todas las visibles"
                      className="h-4 w-4 rounded border-slate-300 text-[#7C5CFC] focus:ring-[#7C5CFC]"
                    />
                  </th>
                  <th className="px-4 py-2">Unidad y residentes</th>
                  <th className="px-4 py-2">Estado</th>
                  <th className="px-4 py-2 text-right">Adeudo</th>
                  <th className="px-4 py-2 text-right">Acceso</th>
                </tr>
              </thead>
              <tbody>
                {units.map((unit) => (
                  <UnitRow
                    key={unit.id}
                    unit={unit}
                    selected={selectedSet.has(unit.id)}
                    pending={pendingAccessId === unit.id}
                    onToggleSelect={onToggleSelect}
                    onToggleAccess={onToggleAccess}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};
